import { LocationInventory } from "../../../features/FloorManager/typings/location-inventory";
import { FileObject } from "../../../features/FileDropzone/typings/FileObject.type";
import { BookingProperty } from "../../FacilityManager/Domain/Types/FloorPlan/BookingProperty.type";
import { WeekdayNames } from "../../FacilityManager/Domain/Types/FloorPlan/WeekdayNames.type";
import { BasicUserInfo } from "../../../features/Reports/typings/occupation-report";

/** Status/Status (Available/Booked/Blocked/Damaged/Maintained) */
export enum FleetStatus {
  Available = "fleet_available",
  Booked = "fleet_booked",
  Blocked = "fleet_blocked"
}

export type FleetManagementPermissions = {
  activeWeekdays: WeekdayNames[];
  companies: { id: string; enforceUsage: boolean }[];
  costCenterUnits: { id: string; enforceUsage: boolean }[];
  users: { id: string; enforceUsage: boolean }[];
  ownerId: number | null;
};

export enum FleetFuelType {
  Gasoline = "fleet_gasoline",
  Diesel = "fleet_diesel",
  Electric = "fleet_electric",
  Hybrid = "fleet_hybrid",
  PlugInHybrid = "fleet_plug_in_hybrid",
  MildHybrid = "fleet_mild_hybrid",
  Hydrogen = "fleet_hydrogen",
  Lpg = "fleet_lpg",
  Cng = "fleet_cng",
  Etc = "fleet_etc"
}

export type FleetAttachment = FileObject & {
  /** Backend id for existing attachments */
  id?: number;
  /** Original filename from backend (for existing attachments) */
  filename?: string;
  /** Storage path for backend attachment */
  path?: string;
  /** Optional description from backend */
  description?: string | null;
  /** When true, attachment is visible only to fleet admins */
  isAdminOnly?: boolean;
  /** Upload timestamp (backend) */
  uploadedAt?: string | Date;
  /** Download URL resolved by backend */
  downloadUrl?: string;
};

export type FleetContractDetails = {
  vendor: string;
  contractId: string;
  start: string;
  end: string;
  leaseRate?: number | null;
};

export type RemoteFleetManagement = {
  // userId: string;
  id: number;
  name: string;
  description: string | null;
  picturePath: string | null | undefined;

  /** License plate/Kennzeichen (i.e. K-RL-1000) */
  licensePlate: string;
  /** Brand model/Markenmodell (i.e. VW Golf Variant) */
  brandModel: string;
  /** Vehicle type/Fahrzeugtyp (i.e. PKW/Kombi/Transporter) */
  vehicleType: string | null;
  /** Fuel/Kraftstoff (Gasoline/Diesel/Electric/Hybrid etc.) */
  fuel: FleetFuelType;
  /** Reach/Reichweite (per tank filling) */
  reach: number;
  /** Number of seats/Anzahl Sitzplätze */
  numSeats: number;
  /**
   * Properties (formerly Accessories/Zubehör – e.g. Navi, parking pass).
   * In the UI we surface these via BookingProperty-based `properties` and label as "Properties".
   * This field is kept for backend compatibility/mapping only.
   */
  accessories?: BookingProperty[];
  /** Location/Standort */
  location?: Partial<LocationInventory>;
  /** Status/Status (Available/Booked/Blocked/Damaged/Maintained) */
  status: FleetStatus;
  /** Current odometer reading/Kilometerstand */
  odometer?: number | null;
  /** Structured contract details (fleet admin only) */
  contractDetails?: FleetContractDetails | null;
  /** Attachments (manuals, contracts, etc.) */
  attachments?: FleetAttachment[];

  permissions: FleetManagementPermissions;

  /** Attributes for status "Blocked" */
  // Reason for Blocking/Sperrbegründung
  blockedReason?: string | null;
  // Time of Blocking/Sperrzeitraum
  blockedTime?: string | null;
};

/**
 * Backend VehicleBlockingEvent (GET/POST /fleet/vehicles/:id/blocking-events, PATCH/DELETE /fleet/blocking-events/:id)
 */
export type VehicleBlockingEvent = {
  id: number;
  vehicleInventoryId: number;
  start: string; // ISO
  end: string; // ISO
  reason?: string | null;
  createdAt?: string;
  createdBy?: string;
  createdByUser?: BasicUserInfo;
};
